import { useEffect, useMemo, useRef, useState } from "react"
import { Alert, Badge, Button, Form, Table } from "react-bootstrap"
import VrboUpdatePropertyOptionsForm, {
    ALL_VRBO_UPDATE_PROPERTY_OPTIONS,
    hasVrboUpdatePropertySelection,
    VrboUpdatePropertyOptions,
} from "../../../Components/VRBOIntegration/VrboUpdatePropertyOptionsForm"
import {
    useEditIntegrationMutation,
    useLazyGetIntegrationsQuery,
    useLazyGetPropertiesListQuery,
    useVrboQueryPropertyMutation,
    useVrboUpdatePropertyAvailabilityMutation,
    useVrboUpdatePropertyMutation,
    useVrboUpdatePropertyPricingMutation,
} from "../../../services/bClientApi"
import { errorMessage, STATUS_VARIANT, StepStatus } from "./vrboMapperShared"

// To add a bulk action: add its key and label here, then a handler in `handlers` inside processIntegration.
const ACTION_KEYS = ['integration', 'property', 'pricing', 'availability'] as const
type ActionKey = typeof ACTION_KEYS[number]

const ACTION_LABELS: Record<ActionKey, string> = {
    integration: 'Update Integration',
    property: 'Update Property',
    pricing: 'Sync Pricing',
    availability: 'Sync Availability',
}

const ACTION_DESCRIPTIONS: Record<ActionKey, string> = {
    integration: 'Pull the latest property data from VRBO and save it on the integration entry',
    property: 'Push property details to VRBO',
    pricing: 'Sync pricing to VRBO',
    availability: 'Sync availability to VRBO',
}

type BulkRow = {
    propertyId: string
    name: string
    steps: Record<ActionKey, StepStatus>
    message?: string
}

function formatDateTime(value?: string | Date | null) {
    if (!value) return 'Never'
    const date = new Date(value)
    return isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString()
}

function integrationName(integration: any) {
    return integration?.rawData?.name || ''
}

// the part of the property title before the first " - " (usually the city or building)
function shortNameFromTitle(title?: string) {
    return title ? title.split(' - ')[0] : ''
}

export default function VrboBulkMode(props: { active: boolean }) {
    const { active } = props

    const [integrations, setIntegrations] = useState<any[]>([])
    // property short names keyed by property id
    const [shortNames, setShortNames] = useState<Record<string, string>>({})
    const [isLoadingList, setIsLoadingList] = useState(false)
    const [listError, setListError] = useState<string | null>(null)
    const hasLoaded = useRef(false)

    const [filter, setFilter] = useState('')
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const [actions, setActions] = useState<Record<ActionKey, boolean>>({
        integration: false,
        property: false,
        pricing: false,
        availability: false,
    })
    const [updateOptions, setUpdateOptions] = useState<VrboUpdatePropertyOptions>(ALL_VRBO_UPDATE_PROPERTY_OPTIONS)

    const [rows, setRows] = useState<BulkRow[]>([])
    const [isRunning, setIsRunning] = useState(false)
    const cancelRef = useRef(false)

    const [fetchIntegrations] = useLazyGetIntegrationsQuery()
    const [fetchProperties] = useLazyGetPropertiesListQuery()
    const [editIntegration] = useEditIntegrationMutation()
    const [queryVRBOProperty] = useVrboQueryPropertyMutation()
    const [updateProperty] = useVrboUpdatePropertyMutation()
    const [updatePricing] = useVrboUpdatePropertyPricingMutation()
    const [updateAvailability] = useVrboUpdatePropertyAvailabilityMutation()

    const loadShortNames = async (docs: any[]) => {
        const propertyIds = docs.map((doc) => doc.entityId).filter(Boolean)
        if (!propertyIds.length) {
            setShortNames({})
            return
        }
        const request = fetchProperties({
            propertiesQuery: { _id: propertyIds },
            pagination: { page: 1, limit: propertyIds.length, select: 'title' },
        }, false)
        try {
            const response = await request.unwrap()
            const names: Record<string, string> = {}
            ;(response?.docs || []).forEach((property: any) => {
                names[property._id] = shortNameFromTitle(property.title)
            })
            setShortNames(names)
        } catch (error) {
            // short names are informational, so the list still works without them
            console.error('VRBO mapper: loading property titles failed', error)
        } finally {
            request.unsubscribe()
        }
    }

    const loadIntegrations = async () => {
        setIsLoadingList(true)
        setListError(null)
        const request = fetchIntegrations({
            integrationsQuery: { entityType: 'property', provider: 'vrbo' },
            pagination: { page: 1, limit: 1000 },
        }, false)
        try {
            const response = await request.unwrap()
            const docs: any[] = response?.docs || []
            setIntegrations(docs)
            // drop selections that no longer exist
            setSelectedIds((current) => new Set(docs.filter((doc) => current.has(doc.entityId)).map((doc) => doc.entityId)))
            await loadShortNames(docs)
        } catch (error) {
            console.error('VRBO mapper: loading integrations failed', error)
            setListError(errorMessage(error))
        } finally {
            // the list is kept in local state, so stop it from refetching every time an integration is edited during a run
            request.unsubscribe()
            setIsLoadingList(false)
        }
    }

    useEffect(() => {
        if (active && !hasLoaded.current) {
            hasLoaded.current = true
            loadIntegrations()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active])

    const filteredIntegrations = useMemo(() => {
        const term = filter.trim().toLowerCase()
        if (!term) return integrations
        return integrations.filter((integration) =>
            [integration.entityId, integration.primaryExternalId, integrationName(integration), shortNames[integration.entityId]]
                .some((value) => `${value || ''}`.toLowerCase().includes(term))
        )
    }, [integrations, filter, shortNames])

    const allFilteredSelected = filteredIntegrations.length > 0 && filteredIntegrations.every((integration) => selectedIds.has(integration.entityId))

    const toggleSelected = (propertyId: string, checked: boolean) => {
        setSelectedIds((current) => {
            const next = new Set(current)
            if (checked) next.add(propertyId)
            else next.delete(propertyId)
            return next
        })
    }

    const toggleAllFiltered = (checked: boolean) => {
        setSelectedIds((current) => {
            const next = new Set(current)
            filteredIntegrations.forEach((integration) => {
                if (checked) next.add(integration.entityId)
                else next.delete(integration.entityId)
            })
            return next
        })
    }

    const updateRow = (index: number, changes: Partial<Omit<BulkRow, 'steps'>> & { steps?: Partial<Record<ActionKey, StepStatus>> }) => {
        setRows((current) => current.map((row, idx) => idx !== index ? row : {
            ...row,
            ...changes,
            steps: { ...row.steps, ...changes.steps },
        }))
    }

    const processIntegration = async (index: number, integration: any, selectedActions: Record<ActionKey, boolean>, options: VrboUpdatePropertyOptions) => {
        const propertyId: string = integration.entityId

        const handlers: Record<ActionKey, () => Promise<unknown>> = {
            integration: async () => {
                const response = await queryVRBOProperty(propertyId).unwrap()
                const vrboProperty = response?.response?.property
                if (!vrboProperty) {
                    throw new Error('No property data returned from VRBO')
                }
                return editIntegration({
                    integrationId: integration._id,
                    integrationInfo: {
                        primaryExternalId: vrboProperty.id,
                        rawData: vrboProperty,
                        updatedAt: new Date(),
                    },
                }).unwrap()
            },
            property: async () => {
                const response = await updateProperty({ propertyId, options }).unwrap()
                // record the push; edit replaces the whole sync object so keep the existing fields
                try {
                    const now = new Date()
                    await editIntegration({
                        integrationId: integration._id,
                        integrationInfo: { sync: { ...integration.sync, lastSyncedAt: now }, updatedAt: now },
                    }).unwrap()
                } catch (error) {
                    console.error('VRBO mapper: updating integration last synced time failed', error)
                }
                return response
            },
            pricing: () => updatePricing(propertyId).unwrap(),
            availability: () => updateAvailability(propertyId).unwrap(),
        }

        for (const key of ACTION_KEYS) {
            if (!selectedActions[key]) continue
            updateRow(index, { steps: { [key]: 'running' } })
            try {
                await handlers[key]()
                updateRow(index, { steps: { [key]: 'success' } })
            } catch (error) {
                console.error(`VRBO mapper: ${key} failed`, error)
                updateRow(index, { steps: { [key]: 'error' }, message: `${ACTION_LABELS[key]}: ${errorMessage(error)}` })
                return
            }
        }
    }

    const handleRun = async () => {
        const selected = integrations.filter((integration) => selectedIds.has(integration.entityId))
        if (!selected.length) return

        // snapshot so changes made while running don't affect the run
        const selectedActions = { ...actions }
        const options = { ...updateOptions }

        cancelRef.current = false
        setRows(selected.map((integration) => ({
            propertyId: integration.entityId,
            name: integrationName(integration),
            steps: ACTION_KEYS.reduce((steps, key) => ({ ...steps, [key]: selectedActions[key] ? 'pending' : 'skipped' }), {} as Record<ActionKey, StepStatus>),
        })))
        setIsRunning(true)

        for (let index = 0; index < selected.length; index++) {
            if (cancelRef.current) break
            await processIntegration(index, selected[index], selectedActions, options)
        }

        setIsRunning(false)
    }

    const selectedCount = selectedIds.size
    const hasAction = ACTION_KEYS.some((key) => actions[key])
    const propertyOptionsValid = !actions.property || hasVrboUpdatePropertySelection(updateOptions)
    const canRun = !isRunning && selectedCount > 0 && hasAction && propertyOptionsValid

    const completedCount = rows.filter((row) => ACTION_KEYS.every((key) => row.steps[key] === 'success' || row.steps[key] === 'skipped')).length
    const failedCount = rows.filter((row) => ACTION_KEYS.some((key) => row.steps[key] === 'error')).length

    return (
        <>
            <div className="d-flex align-items-center gap-2 mb-2">
                <Form.Control
                    size="sm"
                    placeholder="Filter by property ID, VRBO ID or name"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                />
                <Button size="sm" variant="outline-primary" onClick={loadIntegrations} disabled={isLoadingList || isRunning}>
                    {isLoadingList ? 'Loading...' : 'Reload'}
                </Button>
            </div>

            {listError && <Alert variant="danger">Error loading integrations: {listError}</Alert>}

            <div style={{ maxHeight: 400, overflowY: 'auto' }} className="mb-3">
                <Table size="sm" bordered hover className="mb-0">
                    <thead>
                        <tr>
                            <th style={{ width: 40 }}>
                                <Form.Check
                                    type="checkbox"
                                    aria-label="Select all shown"
                                    checked={allFilteredSelected}
                                    onChange={(e) => toggleAllFiltered(e.target.checked)}
                                    disabled={isRunning || !filteredIntegrations.length}
                                />
                            </th>
                            <th>Property ID</th>
                            <th>Short Name</th>
                            <th>VRBO ID</th>
                            <th>Name</th>
                            <th>Sync Status</th>
                            <th>Last Synced</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredIntegrations.map((integration) => (
                            <tr key={integration._id}>
                                <td>
                                    <Form.Check
                                        type="checkbox"
                                        aria-label={`Select ${integration.entityId}`}
                                        checked={selectedIds.has(integration.entityId)}
                                        onChange={(e) => toggleSelected(integration.entityId, e.target.checked)}
                                        disabled={isRunning}
                                    />
                                </td>
                                <td><code>{integration.entityId}</code></td>
                                <td>{shortNames[integration.entityId]}</td>
                                <td><code>{integration.primaryExternalId}</code></td>
                                <td>{integrationName(integration)}</td>
                                <td><Badge bg="secondary">{integration.sync?.status || 'unknown'}</Badge></td>
                                <td>{formatDateTime(integration.sync?.lastSyncedAt)}</td>
                            </tr>
                        ))}
                        {!filteredIntegrations.length && (
                            <tr>
                                <td colSpan={7} className="text-center text-muted">
                                    {isLoadingList ? 'Loading integrations...' : 'No VRBO integrations found'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <div className="border rounded p-3 mb-3">
                <h6>Actions to run</h6>
                {ACTION_KEYS.map((key) => (
                    <div key={key}>
                        <Form.Check
                            id={`vrbo-bulk-action-${key}`}
                            type="checkbox"
                            label={<span><strong>{ACTION_LABELS[key]}</strong> <small className="text-muted">{ACTION_DESCRIPTIONS[key]}</small></span>}
                            checked={actions[key]}
                            onChange={(e) => setActions({ ...actions, [key]: e.target.checked })}
                            disabled={isRunning}
                            className="mb-2"
                        />
                        {key === 'property' && actions.property && (
                            <div className="ms-4 mb-2">
                                <VrboUpdatePropertyOptionsForm
                                    options={updateOptions}
                                    onChange={setUpdateOptions}
                                    idPrefix="vrbo-bulk-update-property"
                                    disabled={isRunning}
                                />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="d-flex align-items-center gap-2 mb-3">
                <Button onClick={handleRun} disabled={!canRun}>
                    {isRunning ? 'Running...' : `Run (${selectedCount} ${selectedCount === 1 ? 'property' : 'properties'})`}
                </Button>
                {isRunning && (
                    <Button variant="outline-danger" onClick={() => { cancelRef.current = true }}>
                        Stop after current property
                    </Button>
                )}
                {rows.length > 0 && (
                    <small className="text-muted">
                        {completedCount} of {rows.length} complete{failedCount > 0 && `, ${failedCount} failed`}
                    </small>
                )}
            </div>

            {rows.length > 0 && (
                <Table size="sm" bordered responsive>
                    <thead>
                        <tr>
                            <th>Property ID</th>
                            <th>Name</th>
                            {ACTION_KEYS.map((key) => (
                                <th key={key}>{ACTION_LABELS[key]}</th>
                            ))}
                            <th>Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.propertyId}>
                                <td><code>{row.propertyId}</code></td>
                                <td>{row.name}</td>
                                {ACTION_KEYS.map((key) => (
                                    <td key={key}>
                                        <Badge bg={STATUS_VARIANT[row.steps[key]]} text={row.steps[key] === 'pending' ? 'dark' : undefined}>
                                            {row.steps[key]}
                                        </Badge>
                                    </td>
                                ))}
                                <td className="text-danger">{row.message}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </>
    )
}
